import { NavLink } from 'react-router-dom'
import {
  Activity,
  BarChart2,
  BookOpen,
  Bot,
  Building2,
  Cpu,
  FileSearch,
  FileText,
  FolderKanban,
  GitBranch,
  Globe,
  Inbox,
  LayoutDashboard,
  Layers,
  Link2,
  ListTodo,
  MessageSquare,
  Network,
  Plug2,
  Repeat,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  Workflow as WorkflowIcon,
  Zap,
  ChevronLeft,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react'

import { useEffectiveRole } from '@/lib/auth/useEffectiveRole'
import type { UserRole } from '@/types/api'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

// ---------------------------------------------------------------------------
// Roles que cobrem todos os tiers admin/operacional. Itens sem `allowedRoles`
// ficam visíveis pra todos os roles (default público).
// ---------------------------------------------------------------------------

const ADMIN_ROLES: ReadonlyArray<UserRole> = [
  'admin',
  'platform_admin',
  'consultant',
  'company_admin',
]

const PLATFORM_ADMIN_ROLES: ReadonlyArray<UserRole> = ['admin', 'platform_admin']

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  /** Quando definido, item só aparece pra esses roles. */
  allowedRoles?: ReadonlyArray<UserRole>
  /** Marca entries que ainda exibem dados mockados — flag visual "MOCK". */
  mock?: boolean
  /** Só visível em dev/staging (esconde em prod build). */
  devOnly?: boolean
}

interface NavSection {
  id: string
  label: string
  items: ReadonlyArray<NavItem>
  /** Quando definido, a section inteira só aparece pra esses roles. */
  allowedRoles?: ReadonlyArray<UserRole>
  devOnly?: boolean
}

// ---------------------------------------------------------------------------
// Catálogo de navegação (7 sections + 1 dev-only)
// ---------------------------------------------------------------------------

const SECTIONS: ReadonlyArray<NavSection> = [
  {
    id: 'estrategia',
    label: 'Estratégia',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/org', label: 'Organograma', icon: Network, allowedRoles: ADMIN_ROLES },
      { to: '/pmo/dashboard', label: 'PMO Engine', icon: LayoutDashboard },
      { to: '/goals', label: 'Objetivos', icon: Target },
      { to: '/projects', label: 'Projetos', icon: FolderKanban },
    ],
  },
  {
    id: 'discovery',
    label: 'Discovery',
    items: [
      { to: '/sipoc/wizard', label: 'Mapear processo', icon: Zap },
      { to: '/sipoc/management', label: 'Processos', icon: Network },
      { to: '/bpmn', label: 'Diagramas BPMN', icon: GitBranch },
      { to: '/knowledge', label: 'Knowledge Base', icon: BookOpen },
      { to: '/sipoc/settings', label: 'Templates SIPOC', icon: Layers },
    ],
  },
  {
    id: 'operacao',
    label: 'Operação',
    items: [
      { to: '/tasks', label: 'Tarefas', icon: ListTodo },
      { to: '/routines', label: 'Rotinas', icon: Repeat },
      { to: '/agents', label: 'Agentes', icon: Users },
      { to: '/inbox', label: 'Inbox', icon: Inbox },
      { to: '/connectors/sessions', label: 'Conversas', icon: MessageSquare },
      { to: '/workflow', label: 'Workflow', icon: WorkflowIcon },
      { to: '/workflow/ops', label: 'Workflow Ops', icon: Activity },
    ],
  },
  {
    id: 'analise',
    label: 'Análise',
    items: [
      { to: '/sipoc/analytics', label: 'Métricas', icon: BarChart2 },
      { to: '/sipoc/report', label: 'Relatório SIPOC', icon: FileSearch },
      { to: '/agents/recommendations', label: 'Recomendações Athena', icon: Sparkles },
      { to: '/analytics/cost', label: 'Custos', icon: BarChart2 },
      { to: '/intelligence', label: 'Inteligência', icon: Globe },
    ],
  },
  {
    id: 'comercial',
    label: 'Comercial',
    items: [
      { to: '/prospects', label: 'Prospects', icon: Building2 },
      { to: '/research-templates', label: 'Templates pesquisa', icon: FileText },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    allowedRoles: PLATFORM_ADMIN_ROLES,
    items: [
      { to: '/admin/agent-builder', label: 'Agent Builder', icon: Bot },
      { to: '/admin/models', label: 'LLM Models', icon: Cpu },
      { to: '/admin/specialties', label: 'Especialidades', icon: BookOpen },
      { to: '/admin/mcp', label: 'MCP Servers', icon: Plug2 },
      { to: '/admin/connectors', label: 'Conectores', icon: Link2 },
    ],
  },
  {
    id: 'governance',
    label: 'Governance',
    allowedRoles: ADMIN_ROLES,
    items: [
      { to: '/council', label: 'Council', icon: ShieldCheck },
      { to: '/audit', label: 'Audit', icon: ScrollText },
    ],
  },
  // Reservado para utilitários de desenvolvimento (MSW debug, feature flags, etc.).
  // Seção oculta enquanto `items` estiver vazio — ver `visibleSectionsFor`.
  {
    id: 'dev',
    label: 'Dev tools',
    devOnly: true,
    items: [],
  },
]

// ---------------------------------------------------------------------------
// Helpers de filtro
// ---------------------------------------------------------------------------

function isAllowed(allowed: ReadonlyArray<UserRole> | undefined, role: UserRole | undefined): boolean {
  if (!allowed) return true
  return role !== undefined && allowed.includes(role)
}

function visibleSectionsFor(role: UserRole | undefined): NavSection[] {
  const isDev = import.meta.env.DEV
  return SECTIONS.filter((section) => {
    if (section.devOnly && !isDev) return false
    if (!isAllowed(section.allowedRoles, role)) return false
    return true
  }).map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (item.devOnly && !isDev) return false
      return isAllowed(item.allowedRoles, role)
    }),
  })).filter((section) => section.items.length > 0)
}

// ---------------------------------------------------------------------------
// NavItem link
// ---------------------------------------------------------------------------

function NavItemLink({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem
  collapsed: boolean
  onNavigate?: (() => void) | undefined
}) {
  const { to, label, icon: Icon, mock } = item
  const link = (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }: { isActive: boolean }) =>
        cn(
          'flex items-center gap-3 rounded-md py-2 text-sm font-medium transition-colors',
          collapsed ? 'justify-center px-2' : 'px-3',
          isActive
            ? cn(
              'bg-secondary text-secondary-foreground',
              !collapsed && 'border-l-2 border-primary pl-[10px]',
            )
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
      {!collapsed && (
        <>
          <span className="truncate">{label}</span>
          {mock && (
            <span
              className="ml-auto rounded border border-warning/40 bg-warning/10 px-1 py-px text-[9px] font-semibold uppercase tracking-wider text-warning dark:text-warning"
              title="Esta página ainda usa dados mockados"
            >
              mock
            </span>
          )}
        </>
      )}
    </NavLink>
  )

  if (!collapsed) return link

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">
        {label}
        {mock ? ' (mock)' : ''}
      </TooltipContent>
    </Tooltip>
  )
}

// ---------------------------------------------------------------------------
// SidebarContent
// ---------------------------------------------------------------------------

interface SidebarContentProps {
  onNavigate?: () => void
  collapsed?: boolean
  onToggleCollapse?: () => void
}

export function SidebarContent({ onNavigate, collapsed = false, onToggleCollapse }: SidebarContentProps) {
  const effectiveRole = useEffectiveRole()
  const sections = visibleSectionsFor(effectiveRole)

  return (
    <TooltipProvider delayDuration={250}>
      <div className="flex h-full flex-col">
        <div
          className={cn(
            'flex h-16 items-center border-b border-border',
            collapsed ? 'justify-center px-2' : 'gap-2 px-6',
          )}
        >
          <Zap className="h-5 w-5 shrink-0 text-primary" />
          {!collapsed && (
            <span className="text-base font-semibold tracking-tight">VectraClip</span>
          )}
        </div>

        <nav className="flex flex-1 flex-col overflow-y-auto p-0">
          {sections.map((section, idx) => (
            <div key={section.id} className={cn('flex flex-col gap-1', idx === 0 ? '' : 'mt-2')}>
              {!collapsed ? (
                // Banner com fundo + borda inferior pra dar contraste e hierarquia
                // visual clara entre as 7 categorias. Texto mais legível
                // (text-foreground/70 + font-bold) que a versão anterior muted/60.
                <div className="sticky top-0 z-[1] flex items-center gap-2 border-y border-border/60 bg-muted/60 px-4 py-1.5 backdrop-blur-sm">
                  <span className="h-1 w-1 rounded-full bg-primary/70" aria-hidden />
                  <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-foreground/70">
                    {section.label}
                  </span>
                </div>
              ) : (
                <div className="mx-2 my-1 border-t border-border/60" />
              )}
              <div className={cn('flex flex-col gap-1', collapsed ? 'px-2' : 'px-3 pb-2 pt-1')}>
                {section.items.map((item) => (
                  <NavItemLink
                    key={item.to}
                    item={item}
                    collapsed={collapsed}
                    onNavigate={onNavigate}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div
          className={cn(
            'flex items-center border-t border-border',
            collapsed ? 'justify-center px-2 py-3' : 'justify-between px-6 py-4',
          )}
        >
          {!collapsed && (
            <span className="text-xs text-muted-foreground">v0.1.0 · M4</span>
          )}
          {onToggleCollapse && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-foreground"
              onClick={onToggleCollapse}
              aria-label={collapsed ? 'Expandir sidebar' : 'Recolher sidebar'}
              title={collapsed ? 'Expandir sidebar' : 'Recolher sidebar'}
            >
              {collapsed ? (
                <ChevronRight className="h-3.5 w-3.5" />
              ) : (
                <ChevronLeft className="h-3.5 w-3.5" />
              )}
            </Button>
          )}
        </div>
      </div>
    </TooltipProvider>
  )
}

// ---------------------------------------------------------------------------
// Sidebar (desktop fixed)
// ---------------------------------------------------------------------------

interface SidebarProps {
  collapsed: boolean
  onToggleCollapse: () => void
}

export function Sidebar({ collapsed, onToggleCollapse }: SidebarProps) {
  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-30 hidden border-r border-border bg-card transition-all duration-200 md:block',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      <SidebarContent collapsed={collapsed} onToggleCollapse={onToggleCollapse} />
    </aside>
  )
}
